"use client";

import React from "react";
import Link from "next/link";
import { X, Plus, MessageSquare, Trash2, User, UserPlus, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useUIStore } from "@/store/ui-store";
import { useChatStore } from "@/store/chat-store";
import { useAuthStore } from "@/store/auth-store";
import { LanguageSelector } from "@/components/ui/language-selector";
import { cn } from "@/lib/utils";

export function Sidebar() {
  const { sidebarOpen, setSidebarOpen } = useUIStore();
  const { chats, currentChatId, createChat, setCurrentChat, deleteChat } = useChatStore();
  const { user, isAuthenticated } = useAuthStore();

  const handleNewChat = () => {
    const newChatId = createChat();
    setCurrentChat(newChatId);
  };

  const handleChatSelect = (chatId: string) => {
    setCurrentChat(chatId);
    // Close sidebar on mobile after selection
    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  };

  const handleDeleteChat = (e: React.MouseEvent, chatId: string) => {
    e.stopPropagation(); // Prevent chat selection when clicking delete

    if (window.confirm("Bu sohbeti silmek istediğinizden emin misiniz?")) {
      deleteChat(chatId);
    }
  };

  const handleLogoClick = () => {
    // Close sidebar on mobile when logo is clicked
    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  };


  if (!sidebarOpen) return null;

  return (
    <>
      {/* Mobile overlay */}
      <div
        className={cn(
          "fixed inset-0 z-40 bg-black/50 md:hidden",
          sidebarOpen ? "block" : "hidden"
        )}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <div
        className={cn(
          "fixed left-0 top-0 z-50 h-full w-80 bg-slate-950 border-r border-slate-800",
          "transform transition-transform duration-300 ease-in-out",
          "md:relative md:z-auto",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-800">
            <Link
              href="/"
              onClick={handleLogoClick}
              className="text-lg font-semibold text-orange-400 hover:text-orange-300 transition-colors"
            >
              AXTROLOG
            </Link>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(false)}
              className="md:hidden"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* New Chat Button */}
          <div className="p-4">
            <Button
              onClick={handleNewChat}
              className="w-full justify-start gap-2"
              variant="outline"
            >
              <Plus className="h-4 w-4" />
              Yeni Sohbet
            </Button>
          </div>

          {/* Chat History */}
          <div className="flex-1 overflow-y-auto px-4 pb-4 scrollbar-thin">
            <div className="space-y-2">
              {chats.map((chat) => (
                <div
                  key={chat.id}
                  className={cn(
                    "group relative rounded-lg transition-colors",
                    currentChatId === chat.id
                      ? "bg-slate-800"
                      : "hover:bg-slate-800"
                  )}
                >
                  <button
                    onClick={() => handleChatSelect(chat.id)}
                    className={cn(
                      "w-full text-left p-3 text-sm transition-colors rounded-lg",
                      currentChatId === chat.id
                        ? "text-slate-50"
                        : "text-slate-400"
                    )}
                  >
                    <div className="flex items-start gap-2 pr-8">
                      <MessageSquare className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium">{chat.title}</p>
                        <p className="text-xs text-slate-400">
                          {chat.messages.length} mesaj
                        </p>
                      </div>
                    </div>
                  </button>

                  {/* Delete Button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => handleDeleteChat(e, chat.id)}
                    className={cn(
                      "absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 opacity-0 group-hover:opacity-100",
                      "transition-opacity hover:bg-red-900/20 hover:text-red-400"
                    )}
                    title="Sohbeti sil"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
              {chats.length === 0 && (
                <div className="text-center text-slate-400 text-sm py-8">
                  Henüz sohbet yok. Yeni bir konuşma başlatın!
                </div>
              )}
            </div>
          </div>

          {/* Mobile Footer - Only visible on mobile */}
          <div className="md:hidden border-t border-slate-800 p-4 space-y-2">
            {/* Language Selector */}
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-slate-400">Dil / Language</span>
              <LanguageSelector variant="flag" />
            </div>

            {/* Free Registration */}
            <Link href="/register" className="w-full">
              <Button
                variant="ghost"
                className="w-full justify-start gap-2 text-green-400 hover:text-green-300 hover:bg-green-900/20"
              >
                <UserPlus className="h-4 w-4" />
                Ücretsiz Kayıt Ol
              </Button>
            </Link>

            {/* User Profile or Login */}
            {isAuthenticated && user ? (
              <div className="flex items-center gap-3 p-2 rounded-lg bg-slate-800">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user.avatar} alt={user.name || user.email} />
                  <AvatarFallback>
                    {user.name ? user.name[0].toUpperCase() : user.email[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">
                    {user.name || user.email}
                  </p>
                  <p className="text-xs text-slate-400 truncate">
                    {user.email}
                  </p>
                </div>
              </div>
            ) : (
              <Link href="/login" className="w-full">
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-2"
                >
                  <User className="h-4 w-4" />
                  Giriş Yap
                </Button>
              </Link>
            )}

            {/* AXTROLOG Nedir */}
            <div className="pt-2 border-t border-slate-800">
              <Link href="/about" className="w-full">
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-2 text-slate-400 hover:text-slate-300 text-xs"
                >
                  <HelpCircle className="h-3 w-3" />
                  AXTROLOG Nedir?
                </Button>
              </Link>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}